var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from '@reduxjs/toolkit';
import { intersection, keyBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAPI, useAuthenticatedAPI } from '../../state';
import { toInt } from '../../util';
import { sortByLOBName } from '../common/autocomplete/group';
import { pickNumberValues } from '../wizard/useDefaultValue';
import { QueueType } from './types';
var MULTI_SKILL_STEP = 2;
var MULTI_SKILL_API = '/api/multiskill/{planId}';
var selectQueues = function (queues) {
  return queues !== null && queues !== void 0 ? queues : [];
};
var selectQueueMap = createSelector(selectQueues, function (queues) {
  return keyBy(queues, 'id');
});
var selectQueueMapByName = createSelector(selectQueues, function (queues) {
  return keyBy(queues, 'name');
});
var selectMultiSkillGroups = createSelector(selectQueues, function (queues) {
  return queues.filter(function (queue) {
    return queue.queueType === QueueType.Multiskill;
  }).map(function (v) {
    var _a;
    return __assign(__assign(__assign({}, pickNumberValues(v, ['id', 'businessArea', 'children', 'multiskillType', 'isUseWAP', 'attritionEntryMode', 'currencyCode'], 'maxOccupancy')), {
      _children: __spreadArray([], v.children, true)
    }), ((_a = v.queueLocations) === null || _a === void 0 ? void 0 : _a.length) ? {
      locations: v.queueLocations
    } : {});
  });
});
export var compareSingleSkillByLob = function (a, b) {
  var _a, _b;
  return sortByLOBName({
    name: a.name,
    lobName: (_a = a.displayLobName) !== null && _a !== void 0 ? _a : undefined
  }, {
    name: b.name,
    lobName: (_b = b.displayLobName) !== null && _b !== void 0 ? _b : undefined
  });
};
var selectSingleQueues = createSelector(selectQueues, function (queues) {
  return queues.filter(function (queue) {
    return queue.id && queue.queueType === QueueType.SingleSkill;
  }).sort(compareSingleSkillByLob);
});
export var selectIsValidGroup = function (ids) {
  return createSelector(selectQueueMap, function (queueMap) {
    var matches = ids.map(function (id) {
      var _a;
      return (_a = queueMap[id]) === null || _a === void 0 ? void 0 : _a.matches;
    });
    return intersection.apply(void 0, __spreadArray([ids], matches, false)).length === ids.length;
  });
};
// Whole-group configured-location intersection. Missing footprints fail closed:
// they are never treated as network-only, so a stale API payload cannot bypass
// the common-location requirement.
export var commonFootprint = function (members) {
  var _a;
  if (!members.length) return {
    valid: true,
    commonLocations: [],
    reason: 'empty'
  };
  if (members.some(function (member) {
    return member.planLocationIds == null;
  })) {
    return {
      valid: false,
      commonLocations: [],
      reason: 'missingFootprint'
    };
  }
  var footprints = members.map(function (member) {
    return member.planLocationIds;
  });
  if (footprints.every(function (footprint) {
    return footprint.length === 0;
  })) {
    return {
      valid: true,
      commonLocations: [],
      reason: 'networkOnly'
    };
  }
  if (footprints.some(function (footprint) {
    return footprint.length === 0;
  })) {
    return {
      valid: false,
      commonLocations: [],
      reason: 'mixed'
    };
  }
  var commonLocations = footprints.reduce(function (acc, footprint) {
    return acc.filter(function (id) {
      return footprint.includes(id);
    });
  }, (_a = footprints[0]) !== null && _a !== void 0 ? _a : []);
  if (commonLocations.length === 0) {
    return {
      valid: false,
      commonLocations: [],
      reason: 'noCommonLocation'
    };
  }
  return {
    valid: true,
    commonLocations: commonLocations,
    reason: 'located'
  };
};
var createNewMultiSkillGroups = function (desQueueByName) {
  return createSelector([selectMultiSkillGroups, selectQueueMap], function (sourceGroups, queueMap) {
    return sourceGroups.map(function (group) {
      return __assign(__assign({}, group), {
        children: group.children.map(function (childId) {
          var _a;
          return (_a = desQueueByName[queueMap[childId].name]) === null || _a === void 0 ? void 0 : _a.id;
        }),
        id: undefined
      });
    });
  });
};
export var useMultiSkillAPI = function () {
  var planId = useParams().id;
  return useAPI(MULTI_SKILL_API, {
    planId: planId
  });
};
export var useCopyMultiSkillAPI = function () {
  var searchParams = useSearchParams()[0];
  var copyScenarioId = searchParams.get('copyScenarioId');
  var _a = useAPI(MULTI_SKILL_API, {
      planId: copyScenarioId
    }),
    data = _a[0],
    fetchData = _a[1],
    clearData = _a[2];
  var fetchDataWrapper = useCallback(function () {
    if (copyScenarioId) {
      fetchData();
    }
  }, [copyScenarioId, fetchData]);
  return [data, fetchDataWrapper, clearData];
};
export var useQueueByIds = function (ids) {
  var data = useMultiSkillAPI()[0].data;
  // Memoize so the returned array keeps a stable reference while `data` and
  // `ids` are unchanged. An unstable array would churn `memberQueueMap` in the
  // group form and re-run the form-reset effect on every render.
  return useMemo(function () {
    return ids.map(function (childId) {
      return selectQueueMap(data)[childId];
    });
  }, [data, ids]);
};
export var useQueueById = function (id) {
  var data = useMultiSkillAPI()[0].data;
  return selectQueueMap(data)[id];
};
export var useSingleSkills = function () {
  var data = useMultiSkillAPI()[0].data;
  return selectSingleQueues(data);
};
export var useMultiSkillGroups = function () {
  var data = useMultiSkillAPI()[0].data;
  return selectMultiSkillGroups(data);
};
export var useGetCopiedGroups = function () {
  var data = useMultiSkillAPI()[0].data;
  var sourceData = useCopyMultiSkillAPI()[0].data;
  var desQueueByName = selectQueueMapByName(data);
  var selectNewGroups = useMemo(function () {
    return createNewMultiSkillGroups(desQueueByName);
  }, [desQueueByName]);
  return selectNewGroups(sourceData);
};
export var useMultiSkillParams = function () {
  var _a = useParams(),
    basePlanId = _a.basePlanId,
    id = _a.id;
  var searchParams = useSearchParams()[0];
  return useMemo(function () {
    var step = Math.max(Math.min(toInt(searchParams.get('step'), 0), MULTI_SKILL_STEP - 1), 0);
    return {
      basePlanId: toInt(basePlanId),
      id: toInt(id),
      step: step
    };
  }, [basePlanId, id, searchParams]);
};
export var useMultiskillFxRatesAPI = function () {
  var id = useParams().id;
  var _a = useAuthenticatedAPI('/api/multiskill/{planId}/fx-rates', {
      planId: id
    }, 'POST'),
    ratesData = _a[0],
    fetchRates = _a[1],
    clearRates = _a[2];
  var fetchRatesWrapper = useCallback(function (currencyCodes) {
    var uniqueCurrencyCodes = Array.from(new Set(currencyCodes));
    fetchRates({
      currencyCodes: uniqueCurrencyCodes
    });
  }, [fetchRates]);
  return [ratesData, fetchRatesWrapper, clearRates];
};